import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShoppingBag, ExternalLink, X, CheckCircle2, Package } from 'lucide-react';

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
   App data — fill in the real details here
   â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const shopifyApps = [
  {
    id: 'mapify',
    name: 'Mapify',
    tagline: 'Google Maps Embedded at Checkout',
    shortDescription: 'Drop a pin. Get the address. Done. Mapify embeds an interactive Google Map directly into the Shopify checkout flow so customers can pinpoint their exact delivery location.',
    fullDescription:
      'Mapify is a Shopify checkout UI extension that places a live Google Map right above the delivery address step. Customers tap to drop a pin, Mapify reverse-geocodes the coordinates into a formatted address, and optionally auto-fills the shipping fields. The pinned location (lat, lng, address, Maps URL) is saved as order metafields — so merchants always know the precise drop point.',
    featurePills: ['Interactive Map', 'Auto-Fill', 'Order Metafields'],
    features: [
      'Live Google Map embedded directly at the checkout delivery step',
      'Automatic address auto-fill via reverse geocoding — zero typing required',
      'Browser geolocation detection to center the map on the customer',
      'Session persistence — pinned location restored on return visits',
      'Lat/lng, formatted address & Maps URL saved to every order as metafields',
      'Non-blocking — checkout always completes even if map is skipped or fails',
      'Usage-based billing: pay only per geocoding call via Shopify Billing API',
      'One-minute setup: one proxy URL in the Checkout Editor, optional defaults',
    ],
    compatibility: ['Shopify', 'Shopify Plus'],
    storeUrl: '#',
    detailsUrl: '/apps/mapify',
    accentColor: '#1A73E8',
    iconBg: '#EBF4FF',
  },
  {
    id: 'ticked',
    name: 'Ticked',
    tagline: 'Task Management Inside Shopify Admin',
    shortDescription: 'Ticked brings task management right into Shopify. Staff log in with a PIN, get a personalised dashboard, and manage tasks linked directly to orders — no extra tools needed.',
    fullDescription:
      'Ticked is a task management app embedded inside Shopify admin. Merchants and their staff can create, assign, and track tasks; link them to Shopify orders; collaborate via comments and checklists; and view work on a Kanban board or list — all without leaving Shopify. Includes PIN-based staff login, role-based access, activity feed, smart filters, and a full audit log.',
    featurePills: ['PIN Login', 'Kanban Board', 'Order-linked Tasks'],
    features: [
      'PIN-based staff login — no passwords, no shared accounts',
      'Role-based access: Admin full control, Staff get exactly what they need',
      'Personalised dashboards: open, overdue, today, in-progress, blocked',
      'Kanban board view with drag-and-drop across status columns',
      'Link tasks directly to Shopify orders in one click',
      'Checklists, comments, labels, priorities, and due dates on every task',
      'Live activity feed so nothing slips through the cracks',
      'Full audit log: every action recorded with staff member and timestamp',
    ],
    compatibility: ['Shopify', 'Shopify Plus'],
    storeUrl: '#',
    detailsUrl: '/apps/ticked',
    accentColor: '#0D9488',
    iconBg: '#F0FDFA',
  },
  {
    id: 'productly',
    name: 'Productly',
    tagline: 'Rich Product Page Enrichment',
    shortDescription: 'Richer product pages, zero code. Productly lets merchants enrich Shopify product pages with custom content blocks, spec tables, trust badges, FAQs, and extra media — all from the admin.',
    fullDescription:
      'Productly is an embedded Shopify admin app that enriches product pages without touching theme code. Merchants design content blocks — spec tables, comparison tables, trust badges, product FAQs, tabbed content, and extra media — assign them to individual products, collections, or the whole catalog, and Productly renders them on the storefront. Usage-based billing via the Shopify Billing API.',
    featurePills: ['No-Code Blocks', 'Spec Tables', 'Trust Badges'],
    features: [
      'Custom content blocks designed in the admin — no theme code',
      'Structured spec tables and side-by-side comparison tables',
      'Trust badges for shipping, warranty, returns, and quality',
      'Inline product FAQs to cut support tickets and hesitation',
      'Tabbed content to keep product pages tidy and scannable',
      'Extra images, videos, and media beyond the default gallery',
      'Assign blocks by product, collection, or the entire catalog',
      'Usage-based billing: pay only for what you use via Shopify Billing API',
    ],
    compatibility: ['Shopify', 'Shopify Plus'],
    storeUrl: '#',
    detailsUrl: '/apps/productly',
    accentColor: '#7C3AED',
    iconBg: '#F3EEFF',
  },
  {
    id: 'upsell-plans-pro',
    name: 'Upsell Plans Pro',
    tagline: 'Protection Plans, Installation & Add-ons on Every Product Page',
    shortDescription: 'Sell extended warranties, installation services, and add-ons right on the product page. Every plan is registered automatically, and customers check coverage and file claims on a self-serve page.',
    fullDescription:
      'Upsell Plans Pro lets merchants sell extended warranties, paid services (installation, assembly, setup), and product add-ons directly on the product page. Every plan sold is registered and tracked automatically with a reference number, and customers look up coverage and file claims on a self-serve protection page. Merchants manage claims and installations from one place — high-margin revenue without the paperwork. Free plan available, no commission.',
    featurePills: ['Protection Plans', 'Installation Booking', 'Claims Portal'],
    features: [
      '1–5 year protection plans with fixed, percentage, or price-band pricing',
      'Installation and service booking with dates, notes, and service areas',
      'Product add-ons from your existing catalog next to the main product',
      'Designable widget: 4 layouts, live preview, 11 languages, any theme',
      'Second-chance offers: add-to-cart popup and one-tap cart protection',
      'Self-serve customer protection page for coverage lookups and claims',
      'Claims inbox and installation pipeline with installer scheduling',
      'Analytics, automatic emails, Shopify Flow triggers, and CSV export',
    ],
    compatibility: ['Shopify', 'Shopify Plus'],
    storeUrl: '#',
    detailsUrl: '/apps/upsell-plans-pro',
    accentColor: '#D97706',
    iconBg: '#FFFBEB',
  },
];

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
   App Card
   â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const AppCard = ({ app, onOpen }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    whileHover={{ y: -4, boxShadow: '0 16px 48px rgba(0,0,0,0.10)' }}
    onClick={() => onOpen(app)}
    className="group bg-white border border-[#E2E8F0] cursor-pointer flex flex-col transition-all duration-300 overflow-hidden hover:border-[#C7D2FE]"
    style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
  >
    {/* Top colour strip */}
    <div className="h-1.5 w-full" style={{ backgroundColor: app.accentColor }} />

    <div className="p-8 flex flex-col flex-1">
      {/* Icon + Shopify badge row */}
      <div className="flex items-start justify-between mb-6">
        <div
          className="w-16 h-16 flex items-center justify-center border border-[#E2E8F0] shadow-sm"
          style={{ backgroundColor: app.iconBg }}
        >
          <ShoppingBag size={28} style={{ color: app.accentColor }} />
        </div>
        <span
          className="px-2.5 py-1 text-[0.6rem] font-mono tracking-widest uppercase border"
          style={{ color: app.accentColor, borderColor: `${app.accentColor}35`, backgroundColor: app.iconBg }}
        >
          Shopify App
        </span>
      </div>

      {/* Name + tagline */}
      <h3 className="font-display text-xl font-bold text-[#0F172A] mb-1.5 group-hover:text-[#2563EB] transition-colors duration-300">
        {app.name}
      </h3>
      <p className="text-[#374151] text-xs font-mono tracking-wide mb-4">{app.tagline}</p>

      {/* Short description */}
      <p className="text-[#374151] text-sm leading-relaxed mb-6 flex-1">
        {app.shortDescription}
      </p>

      {/* Feature pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        {app.featurePills.map((pill) => (
          <span
            key={pill}
            className="px-3 py-1 text-xs rounded-full"
            style={{ backgroundColor: app.iconBg, color: app.accentColor }}
          >
            {pill}
          </span>
        ))}
      </div>

      {/* Footer CTA */}
      <div className="flex items-center justify-between pt-5 border-t border-[#E2E8F0]">
        <span className="text-sm font-medium" style={{ color: app.accentColor }}>
          View App Details
        </span>
        <ExternalLink size={15} style={{ color: app.accentColor }} />
      </div>
    </div>
  </motion.div>
);

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
   Modal
   â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const AppModal = ({ app, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-start justify-center px-4 py-10 overflow-y-auto"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className="relative bg-white border border-[#E2E8F0] w-full max-w-2xl shadow-2xl z-10 overflow-hidden"
        style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.18)' }}
      >
        {/* Top colour strip */}
        <div className="h-2 w-full" style={{ backgroundColor: app.accentColor }} />

        {/* Header */}
        <div className="flex items-start justify-between p-8 pb-6 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-5">
            <div
              className="w-16 h-16 flex items-center justify-center border border-[#E2E8F0] shadow-sm"
              style={{ backgroundColor: app.iconBg }}
            >
              <ShoppingBag size={28} style={{ color: app.accentColor }} />
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold text-[#0F172A] mb-1">{app.name}</h2>
              <span
                className="px-2.5 py-1 text-[0.6rem] font-mono tracking-widest uppercase border inline-block"
                style={{ color: app.accentColor, borderColor: `${app.accentColor}35`, backgroundColor: app.iconBg }}
              >
                Shopify App Store
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-[#374151] hover:text-[#0F172A] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-8 space-y-8">
          {/* About */}
          <div>
            <h4 className="font-mono text-[0.6rem] tracking-widest text-[#374151] uppercase mb-3">About This App</h4>
            <p className="text-[#374151] text-sm leading-relaxed">{app.fullDescription}</p>
          </div>

          {/* Key features */}
          <div>
            <h4 className="font-mono text-[0.6rem] tracking-widest text-[#374151] uppercase mb-3">Key Features</h4>
            <div className="space-y-2.5">
              {app.features.map((feat) => (
                <div key={feat} className="flex items-start gap-3">
                  <CheckCircle2 size={15} style={{ color: app.accentColor }} className="shrink-0 mt-0.5" />
                  <span className="text-[#374151] text-sm">{feat}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Compatibility */}
          <div>
            <h4 className="font-mono text-[0.6rem] tracking-widest text-[#374151] uppercase mb-3">Compatibility</h4>
            <div className="flex gap-2 flex-wrap">
              {app.compatibility.map((c) => (
                <span
                  key={c}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#95BF47]/25 text-[#5E8E3E] bg-[#F0F7E6]"
                >
                  <Package size={12} />
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="px-8 py-6 border-t border-[#E2E8F0] bg-[#F8F7F4] flex flex-col sm:flex-row items-center gap-4">
          {app.detailsUrl ? (
            <Link
              to={app.detailsUrl}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white"
              style={{ backgroundColor: app.accentColor }}
              onClick={() => { onClose(); window.scrollTo(0, 0); }}
            >
              View Full Details
              <ExternalLink size={14} />
            </Link>
          ) : (
            <a
              href={app.storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white"
              style={{ backgroundColor: app.accentColor }}
            >
              View on Shopify App Store
              <ExternalLink size={14} />
            </a>
          )}
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-[#374151] border border-[#E2E8F0] bg-white hover:border-[#0F172A]/20 hover:text-[#0F172A] transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
   Section
   â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export const ShopifyAppsSection = () => {
  const [selectedApp, setSelectedApp] = useState(null);

  return (
    <section id="shopify-apps" className="section-padding bg-[#F8F7F4]" data-testid="shopify-apps-section">
      <div className="container-custom">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="section-label">Published on Shopify</span>
            <span className="w-6 h-px bg-[#2563EB]/30" />
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2
              className="font-display font-bold text-[#0F172A]"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              Our Shopify <span className="italic text-gold">Apps</span>
            </h2>
            <p className="text-[#374151] max-w-sm leading-relaxed text-sm lg:text-right">
              Purpose-built Shopify apps developed by our team — click any card to explore features and details.
            </p>
          </div>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {shopifyApps.map((app) => (
            <AppCard key={app.id} app={app} onOpen={setSelectedApp} />
          ))}
        </div>

        {/* Help note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center font-mono text-[0.6rem] tracking-widest text-[#9CA3AF] uppercase"
        >
          Click any card to view full app details
        </motion.p>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedApp && (
          <AppModal app={selectedApp} onClose={() => setSelectedApp(null)} />
        )}
      </AnimatePresence>
    </section>
  );
};
